<template>
  <div class="dossier-container">
    <!-- Dossier state banner -->
    <div class="dossier-banner">
      <div class="dossier-info">
        <h3
          v-if="!hasDossierContent"
          class="dossier-title"
        >
          Your Dossier is empty
        </h3>
        <h3
          v-else
          class="dossier-title"
        >
          Your Dossier
        </h3>
        <p
          v-if="!hasDossierContent"
          class="dossier-subtitle"
        >
          To generate a dossier, add some data sources below:
        </p>
        <p
          v-else
          class="dossier-subtitle"
        >
          Your dossier contains data from the sources below
        </p>
      </div>
      <div class="dossier-action tw-flex tw-flex-col tw-items-center">
        <Button
          variant="primary"
          icon-before="fas fa-sync"
          :loading="loading || !ready"
          :disabled="loading || !ready || !hasAnyData"
          @click="emit('process-data')"
        >
          <span v-if="!hasDossierContent">Generate Dossier</span>
          <span v-else>Update Dossier</span>
        </Button>
        <span class="dossier-subtitle" v-if="loading">Updating Dossier...</span>
      </div>
    </div>

    <!-- Entity data grid -->
    <div class="component">
      <div class="item">
        <p class="item-title">
          Attach Documents to this entity
        </p>
        <p
          v-if="!documentCount"
          class="item-description"
        >
          Upload W-2s, 1099s, PDFs, ID documents, and more
        </p>
        <Badges gap="2">
          <template v-if="hasFetchedDocuments">
            <Badge
              v-if="documentCount"
              status="success"
            >
              <strong>{{ documentCount }}</strong> Files uploaded
            </Badge>
            <Button
              variant="badge"
              icon-before="fas fa-upload"
              :disabled="!ready"
              @click="showDocumentModal = true"
            >
              <span v-if="documentCount">Upload more</span>
              <span v-else>Choose a File</span>
            </Button>
          </template>

          <Badge
            v-else
            status="disabled"
            height-only
          >
            Loading…
          </Badge>
        </Badges>
      </div>

      <div class="item">
        <p class="item-title">
          Link Meetings to this entity
        </p>

        <template v-if="advisorStore.hasFeature('Meetings')">
          <p
            v-if="!dossierStore.linkedMeetingCount"
            class="item-description"
          >
            Attach past or upcoming meetings
          </p>
          <Badges gap="2">
            <Badge
              v-if="dossierStore.linkedMeetingCount"
              status="success"
            >
              <strong>{{ dossierStore.linkedMeetingCount }}</strong> Linked
            </Badge>
            <Button
              variant="badge"
              icon-before="fas fa-plus"
              :disabled="!ready"
              @click="showMeetingsModal = true"
            >
              <span v-if="dossierStore.linkedMeetingCount">Link more</span>
              <span v-else>Select meetings</span>
            </Button>
          </Badges>
        </template>

        <template v-else>
          <p class="item-description">
            Add YourStake's Meeting Assistant to include data from meeting notes in your Dossier.
          </p>
            <Button variant="badge" href="https://yourstake.org/product-tours/ai-note-taker" newTab>Learn More!</Button>
        </template> 
      </div>

      <div class="item">
        <p class="item-title">
          Link to a CRM contact
        </p>
        <p
          v-if="!crmContactName"
          class="item-description"
        >
          Import data from your CRM
        </p>
        <Badges gap="2">
          <Badge
            v-if="crmContactName"
            status="info"
          >
            {{ crmContactName }}
          </Badge>
          <Button
            v-if="crmContactName"
            variant="badge"
            icon-before="fas fa-unlink"
            :disabled="!ready"
            @click="unlinkCrmContact"
          >
            Unlink
          </Button>
          <Button
            v-else
            variant="badge"
            icon-before="fas fa-link"
            :disabled="!ready"
            @click="showCrmLinkingModal = true"
          >
            Choose a CRM contact
          </Button>
        </Badges>
      </div>
    </div>

    <!-- Document Modal -->
    <StakeModal
      v-model="showDocumentModal"
      no-max-width
    >
      <Stack class="master-upload">
        <DocMasterUpload
          :entity-external-id="entityExternalId"
          :csrf-token="csrfToken"
          is-modal
          @documents-updated="fetchPastDocuments"
        />
      </Stack>
    </StakeModal>

    <!-- CRM Linking Modal -->
    <StakeModal v-model="showCrmLinkingModal">
      <CrmLinkingModal
        v-if="showCrmLinkingModal"
        :entity-id="entityExternalId"
        message="Please select a CRM contact to link to this entity."
        @contact-linked="handleContactLinked"
        @close="showCrmLinkingModal = false"
      />
    </StakeModal>

    <SelectMeetings
      v-model="showMeetingsModal"
      :entity-external-id="entityExternalId"
    />
  </div>
</template>

<script setup>
import { onMounted, ref, computed } from 'vue';
import { Badge, Badges, Button, Stack, StakeModal } from '@stakeui';
import CrmLinkingModal from './CrmLinkingModal.vue';
import SelectMeetings from '@features/dossier/components/SelectMeetings.vue';

import { useAdvisorStore } from '@frontend/ui/stores/advisorStore';
const advisorStore = useAdvisorStore();

import { useDossierStore } from '@frontend/ui/stores/dossierStore';
const dossierStore = useDossierStore();
import DocMasterUpload from './DocMasterUpload.vue';

const props = defineProps({
  ready: {
    type: Boolean,
    default: false,
  },
  meetingCount: {
    type: Number,
    default: 0,
  },
  crmContactName: {
    type: String,
    default: null,
  },
  entityExternalId: {
    type: String,
    required: true,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  csrfToken: {
    type: String,
  },
  hasDossierContent: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['crm-contact-updated', 'process-data']);

const csrfToken = ref('');
const showDocumentModal = ref(false);
const hasFetchedDocuments = ref(false);
const documentCount = ref(0);
const showCrmLinkingModal = ref(false);
const showMeetingsModal = ref(false);

const hasAnyData = computed(() => {
  return documentCount.value > 0 || dossierStore.linkedMeetingCount > 0 || props.crmContactName;
});

const handleContactLinked = data => {
  showCrmLinkingModal.value = false;
  // Signal to the parent component to update the entity data
  emit('crm-contact-updated');
  dossierStore.lastRefresh = Date.now();
};

const unlinkCrmContact = async () => {
  try {
    const response = await fetch(`/api/v1/entities/${props.entityExternalId}/crm-contact/`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': document.querySelector('[name="csrfmiddlewaretoken"]')?.value,
      },
    });

    if (response.ok) {
      // Signal to the parent component to update the entity data
      emit('crm-contact-updated');
      dossierStore.lastRefresh = Date.now();
    } else {
      console.error('Failed to unlink CRM contact:', response.statusText);
      alert('Failed to unlink CRM contact. Please try again.');
    }
  } catch (error) {
    console.error('Error unlinking CRM contact:', error);
    alert('An error occurred while unlinking the CRM contact. Please try again.');
  }
};

const fetchPastDocuments = async () => {
  hasFetchedDocuments.value = false;

  try {
    const response = await fetch(`/api/v1/advisor-documents/${props.entityExternalId}`, {
      headers: {
        'X-CSRFToken': document.querySelector('[name="csrfmiddlewaretoken"]')?.value,
      },
    });
    const data = await response.json();
    documentCount.value = data.documents.length;
  } catch (error) {
    console.error('Error fetching past documents:', error);
  } finally {
    hasFetchedDocuments.value = true;
  }
};

onMounted(() => {
  csrfToken.value = document.querySelector('[name="csrfmiddlewaretoken"]')?.value;
  fetchPastDocuments();
});
</script>

<style scoped>
.dossier-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.dossier-banner {
  background-color: var(--stk-primary-95);
  border-radius: 1em;
  border: 1px solid var(--stk-primary-80);
  padding: 1.25em 1.5em;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.dossier-title {
  margin: 0;
  font-weight: 600;
  color: var(--stk-text-strong);
}

.dossier-subtitle {
  color: var(--stk-primary-40);
  font-size: 0.875em;
}

.component {
  background-color: var(--stk-background);
  border-radius: 1em;
  border: 1px solid var(--stk-border);
  padding: 1.25em 1.5em;
  display: grid;
  gap: 1em;
  grid-template-columns: repeat(3, 1fr);
}

.item-title {
  color: var(--stk-text-strong);
  font-size: 0.875em;
  font-weight: 600;
  margin-bottom: 0.25em;
}

.item-description {
  color: var(--stk-text-soft);
  font-size: 0.875em;
  margin: 0 0 0.5em;
}

.master-upload {
  width: 60rem;
}
</style>
